import React, { useState, useEffect } from 'react';
import { obtenerTodosControlPagos, procesarTransferencia } from '../../services/pagosServices';
import { Loader, Check, X, ExternalLink, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Funciones de notificación estandarizadas
const showSuccessToast = (message) => {
    toast.success(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        }
    });
};

const showErrorToast = (message) => {
    toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        }
    });
};

const showWarningToast = (message) => {
    toast.warning(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        }
    });
};

const MostrarTransferencias = () => {
    const [pagos, setPagos] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [imagenSeleccionada, setImagenSeleccionada] = useState(null);
    const [procesandoPagos, setProcesandoPagos] = useState({});
    const [busqueda, setBusqueda] = useState('');
    const [gruposExpandidos, setGruposExpandidos] = useState({});

    useEffect(() => {
        const cargarPagos = async () => {
            try {
                const response = await obtenerTodosControlPagos();
                if (response.success) {
                    const pagosPorTransferencia = response.data.filter(
                        pago => pago.tipo_pago === 'Transferencia'
                    );
                    setPagos(pagosPorTransferencia);
                }
            } catch (err) {
                setError('Error al cargar los pagos: ' + err.message);
            } finally {
                setIsLoading(false);
            }
        };

        cargarPagos();
    }, []);

    const convertirBufferAImagen = (buffer) => {
        if (!buffer || !buffer.data) return null;
        const bytes = new Uint8Array(buffer.data);
        let binary = '';
        bytes.forEach(byte => binary += String.fromCharCode(byte));
        return binary;
    };

    const formatearPeriodoPago = (fecha) => {
        const fechaInicio = new Date(fecha);
        const fechaFin = new Date(fecha);
        fechaFin.setMonth(fechaFin.getMonth() + 1);

        const formatearFecha = (date) => {
            const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 
                          'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
            return `${meses[date.getMonth()]} ${date.getFullYear()}`;
        };

        return `${formatearFecha(fechaInicio)} a ${formatearFecha(fechaFin)}`;
    };

    const aprobarTransferencia = async (pagosGrupo) => {
        const codigoTransferencia = pagosGrupo[0].codigo_transferencia || pagosGrupo[0].id_control;
        setProcesandoPagos(prev => ({ ...prev, [codigoTransferencia]: true }));
        try {
            for (const pago of pagosGrupo) {
                console.log('Aprobando transferencia:', { id_control: pago.id_control, accion: 'aprobar' });
                await procesarTransferencia(pago.id_control, 'aprobar');
            }
            showSuccessToast('Transferencia aprobada exitosamente');
            const pagosResponse = await obtenerTodosControlPagos();
            if (pagosResponse.success) {
                const pagosPorTransferencia = pagosResponse.data.filter(
                    pago => pago.tipo_pago === 'Transferencia'
                );
                setPagos(pagosPorTransferencia);
            }
        } catch (error) {
            console.error('Error al aprobar transferencia:', error);
            showErrorToast(error.message || 'Error al aprobar transferencia');
        } finally {
            setProcesandoPagos(prev => ({ ...prev, [codigoTransferencia]: false }));
        }
    };

    const rechazarTransferencia = async (pagosGrupo) => {
        const codigoTransferencia = pagosGrupo[0].codigo_transferencia || pagosGrupo[0].id_control;
        setProcesandoPagos(prev => ({ ...prev, [codigoTransferencia]: true }));
        try {
            for (const pago of pagosGrupo) {
                console.log('Rechazando transferencia:', { id_control: pago.id_control, accion: 'rechazar' });
                await procesarTransferencia(pago.id_control, 'rechazar');
            }
            showSuccessToast('Transferencia rechazada exitosamente');
            const pagosResponse = await obtenerTodosControlPagos();
            if (pagosResponse.success) {
                const pagosPorTransferencia = pagosResponse.data.filter(
                    pago => pago.tipo_pago === 'Transferencia'
                );
                setPagos(pagosPorTransferencia);
            }
        } catch (error) {
            console.error('Error al rechazar transferencia:', error);
            showErrorToast(error.message || 'Error al rechazar transferencia');
        } finally {
            setProcesandoPagos(prev => ({ ...prev, [codigoTransferencia]: false }));
        }
    };

    const toggleGrupoExpandido = (codigo) => {
        setGruposExpandidos(prev => ({
            ...prev,
            [codigo]: !prev[codigo]
        }));
    };

    const pagosFiltrados = pagos.filter(pago => 
        pago.nino.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
        pago.nino.apellidos.toLowerCase().includes(busqueda.toLowerCase()) ||
        pago.padre.nombre.toLowerCase().includes(busqueda.toLowerCase())
    );

    // Agrupar pagos por código de transferencia
    const pagosAgrupados = pagosFiltrados.reduce((grupos, pago) => {
        const codigo = pago.codigo_transferencia || 'individual';
        if (!grupos[codigo]) {
            grupos[codigo] = [];
        }
        grupos[codigo].push(pago);
        return grupos;
    }, {});

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <Loader className="w-8 h-8 animate-spin text-blue-600" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4 text-red-600 text-center">
                {error}
            </div>
        );
    }

    return (
        <>
            <ToastContainer 
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                className="mt-16"
                style={{ zIndex: 9999 }}
            />
            
            <div className="container mx-auto px-2 sm:px-6">
                <h2 className="text-2xl sm:text-3xl font-light mb-4 sm:mb-8 text-gray-800 text-center">
                    Transferencias Pendientes
                </h2>

                {pagos.length > 0 && (
                    <div className="max-w-md mx-auto mb-4 sm:mb-8">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 sm:w-5 h-4 sm:h-5" />
                            <input
                                type="text"
                                placeholder="Buscar por nombre del niño o padre..."
                                value={busqueda}
                                onChange={(e) => setBusqueda(e.target.value)}
                                className="w-full pl-10 sm:pl-12 pr-4 py-2 sm:py-3 rounded-xl bg-white border border-gray-400 focus:ring-2 focus:ring-indigo-500 transition-all duration-300 outline-none text-sm sm:text-base"
                            />
                        </div>
                    </div>
                )}

                {Object.keys(pagosAgrupados).length === 0 ? (
                    <div className="p-4 text-gray-600 text-center bg-gray-50 rounded-xl">
                        No se encontraron transferencias que coincidan con la búsqueda.
                    </div>
                ) : (
                    <div className="grid gap-4 sm:gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                        {Object.entries(pagosAgrupados).map(([codigoTransferencia, pagosGrupo]) => {
                            const primerPago = pagosGrupo[0];
                            const estaProcesando = procesandoPagos[codigoTransferencia] || procesandoPagos[primerPago.id_control];
                            
                            return (
                                <div key={codigoTransferencia} className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden">
                                    <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                                        <div className="flex items-start sm:items-center justify-between">
                                            <div>
                                                <h3 className="text-base sm:text-lg font-medium text-gray-900">
                                                    {codigoTransferencia !== 'individual' ? 
                                                        `Grupo de transferencia (${pagosGrupo.length})` : 
                                                        `${primerPago.nino.nombre} ${primerPago.nino.apellidos}`
                                                    }
                                                </h3>
                                                <p className="text-xs sm:text-sm text-gray-500">{primerPago.padre.nombre}</p>
                                            </div>
                                            <span className="px-2 sm:px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                {primerPago.estado}
                                            </span>
                                        </div>

                                        {codigoTransferencia !== 'individual' && (
                                            <div className="text-xs sm:text-sm text-gray-600">
                                                <p className="font-medium mb-1">Niños incluidos:</p>
                                                <ul className="list-disc pl-4 space-y-0.5">
                                                    {pagosGrupo.map(pago => (
                                                        <li key={pago.id_control}>
                                                            {pago.nino.nombre} {pago.nino.apellidos}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        <div className="space-y-2 text-xs sm:text-sm text-gray-600">
                                            <p><span className="text-gray-500">Contacto:</span> {primerPago.padre.email}</p>
                                            <p><span className="text-gray-500">Teléfono:</span> {primerPago.padre.telefono}</p>
                                            <p><span className="text-gray-500">Monto Total:</span> ${pagosGrupo.reduce((sum, p) => sum + parseFloat(p.monto), 0).toFixed(2)}</p>
                                            <p><span className="text-gray-500">Período:</span> {formatearPeriodoPago(primerPago.proxima_fecha_pago)}</p>
                                        </div>

                                        {primerPago.imagen_comprobante && (
                                            <div 
                                                className="relative group cursor-pointer"
                                                onClick={() => setImagenSeleccionada(primerPago.imagen_comprobante)}
                                            >
                                                <img 
                                                    src={primerPago.imagen_comprobante}
                                                    alt="Comprobante de pago"
                                                    className="w-full h-36 sm:h-48 object-cover rounded-lg"
                                                />
                                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 rounded-lg flex items-center justify-center">
                                                    <ExternalLink className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                                </div>
                                            </div>
                                        )}

                                        <div className="flex gap-2 sm:gap-3 pt-2">
                                            {estaProcesando ? (
                                                <div className="flex-1 flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-gray-100 rounded-lg">
                                                    <Loader className="w-4 h-4 animate-spin text-gray-600" />
                                                    <span className="text-xs sm:text-sm text-gray-600">Procesando...</span>
                                                </div>
                                            ) : (
                                                <>
                                                    <button
                                                        onClick={() => aprobarTransferencia(pagosGrupo)}
                                                        className="flex-1 flex items-center justify-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-xs sm:text-sm"
                                                    >
                                                        <Check size={16} />
                                                        Aprobar
                                                    </button>
                                                    <button
                                                        onClick={() => rechazarTransferencia(pagosGrupo)}
                                                        className="flex-1 flex items-center justify-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-xs sm:text-sm"
                                                    >
                                                        <X size={16} />
                                                        Rechazar
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {imagenSeleccionada && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50"
                    onClick={() => setImagenSeleccionada(null)}
                >
                    <div className="max-w-[90vw] max-h-[90vh] w-auto h-auto">
                        <img 
                            src={imagenSeleccionada}
                            alt="Comprobante de pago ampliado"
                            className="w-auto h-auto max-w-full max-h-full rounded-lg"
                            style={{objectFit: 'contain'}}
                        />
                    </div>
                </div>
            )}
        </>
    );
};

export default MostrarTransferencias;
